import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, FormControl, InputLabel, Select, MenuItem, Box, Paper, Divider, Chip, CircularProgress, IconButton, ThemeProvider, createTheme, Stack } from "@mui/material";
import { useState } from "react";
import CustomBtnFilled from "../../atoms/form-molecules/CustomBtnFilled";
import { APIRoutes } from "../../constants";
import { RequestServer } from "../../utils/services";
import { getUserId, isSuperAdmin } from "../../utils/role";
import { IdentityVerifications } from "../../constants/veriication.constants";
import VerifiedIcon from '@mui/icons-material/Verified';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import BadgeIcon from '@mui/icons-material/Badge';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from "notistack";
import { commonInputStyles } from "../../common/input-styles";
import { HookTextField } from "../../atoms/form-fields/SLFieldTextField";
import FormCloseWarningDialog from "../../molecules/form-close-warning";
import { defaultFormTheme } from "../../atoms/defaultFormTheme";
import { useExpertProfileContext } from "../../organisms/expert-profile/context";

interface VerifyExpertDialogProps {
  open: boolean;
  onClose: () => void;
  expertId: string;
}

const VerifyExpertDialog = ({ open, onClose, expertId }: VerifyExpertDialogProps) => {
  const [verifySource, setVerifySource] = useState("LinkedIn");
  const [openAlertBox, setAlertBox] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setVerificationDetails, verificationDetails } = useExpertProfileContext()
  const userId = getUserId() || "0";
  const { enqueueSnackbar } = useSnackbar();

  const handleVerifyClick = async () => {
    try {
      setLoading(true);
      const verificationData = {
        card_id: Number(expertId),
        table_name: "experts",
        type: "Identity",
        source: verifySource,
        is_manual: true,
        verified_by: userId
      };

      const response: any = await RequestServer(
        APIRoutes.VERIFY_EXPERT,
        "POST",
        verificationData
      );
      const formattedDate = new Date(response.data?.updated_at).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      setVerificationDetails((prev: any) => {
        return {
          ...prev,
          isVerified: true,
          verified_by: response.data?.verifier_name,
          verified_on: formattedDate || '',
          type: response.data?.type,
          source: response.data?.source
        }
      })
      setLoading(false);
      enqueueSnackbar(response.message, {
        variant: "success",
      });
      onClose();
    } catch (error) {
      console.log(error);
      setLoading(false);
      enqueueSnackbar("Something Went Wrong", {
        variant: "warning",
      });
    }
  }

  const handleUnverifyClick = async () => {
    try {
      setLoading(true);
      const verificationData = {
        card_id: Number(expertId),
        table_name: "experts",
        type: verificationDetails.type,
        source:
          verificationDetails.source,
        is_manual: true,
        verified_by: userId
      };

      const response: any = await RequestServer(
        APIRoutes.VERIFY_EXPERT,
        "DELETE",
        verificationData
      );
      setVerificationDetails({
        isVerified: false,
        verified_by: '',
        verified_on: '',
        type: '',
        source: ''
      })
      setLoading(false);
      enqueueSnackbar(response.message, {
        variant: "success",
      });
      onClose();
    } catch (error) {
      console.log(error);
      setLoading(false);
      enqueueSnackbar("Something Went Wrong", {
        variant: "warning",
      });
    }
  }
  const handleAlertBoxYesClick = () => {
    setAlertBox(() => false)
    handleUnverifyClick();
  };
  const defaultTheme = createTheme(defaultFormTheme);

  return (
    <ThemeProvider theme={defaultTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          style: {
            borderRadius: '12px',
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 0.5,
          pt: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Verify Expert</Typography>
          </Box>
          <IconButton 
            aria-label="close" 
            onClick={onClose} 
            size="small" 
            sx={{ 
              color: 'grey.500',
              '&:hover': {
                color: 'grey.700',
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 1.5 }}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 1.5,
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ width: '200px' }}>
                  Expert ID:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {expertId}
                </Typography>
              </Box>
              {verificationDetails.type && <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ width: '200px' }}>
                  Verification Type:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verificationDetails.type}
                </Typography>
              </Box>}
              {verificationDetails.verified_by && <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ width: '200px' }}>
                  Verified By:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verificationDetails.verified_by}
                </Typography>
              </Box>}
              {verificationDetails.source && <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ width: '200px' }}>
                  Source:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verificationDetails.source}
                </Typography>
              </Box>}
            </Box>
          </Paper>
          <FormCloseWarningDialog
            handleClose={() => setAlertBox(false)}
            handleYesClick={handleAlertBoxYesClick}
            open={openAlertBox}
            text="Do you really want to change this expert's status to unverified?"
          />
          {verificationDetails.isVerified && (
            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
              <Typography color="black" fontWeight={600} textAlign={'center'}>Expert is already verified</Typography>
              {
                isSuperAdmin() && <CustomBtnFilled
                  label={loading ? "Unverifying..." : "Mark As Unverified"}
                  onClick={() => setAlertBox(true)}
                  variant="contained"
                  styles={{
                    background:'red'
                  }}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <UnpublishedIcon />}
                  disabled={loading}
                  sx={{ borderRadius: '8px', py: 0.5 }}
                />
              }
            </Stack>
          )}

          {
            !verificationDetails.isVerified && (
              <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                <InputLabel id="verify-source-label" sx={{ fontSize: '0.875rem' }}>Verified from</InputLabel>
                <Select
                  labelId="verify-source-label"
                  value={verifySource}
                  label="Verified from"
                  onChange={(e) => setVerifySource(e.target.value)}
                  disabled={loading}
                  size="small"
                  variant="outlined"
                >
                  {IdentityVerifications.map((source) => (
                    <MenuItem key={source} value={source} dense>
                      {source}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )
          }
        </DialogContent>
        {!verificationDetails.isVerified && (
          <DialogActions sx={{ px: 2.5, py: 1.5 }}>
            <CustomBtnFilled
              label="Cancel"
              onClick={onClose}
              variant="outlined"
              startIcon={<CloseIcon />}
              disabled={loading}
              sx={{ borderRadius: '8px', py: 0.5 }}
            />
            <CustomBtnFilled
              label={loading ? "Verifying..." : "Mark As Verified"}
              onClick={handleVerifyClick}
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <VerifiedIcon />}
              disabled={loading}
              sx={{ borderRadius: '8px', py: 0.5 }}
            />
          </DialogActions>
        )}
      </Dialog>
    </ThemeProvider>
  );
};

export default VerifyExpertDialog;